import * as React from "react";
import type { EntitySelectorHandle } from "./entity-selector-root";

/**
 * Arrow/Enter navigation for a selector whose focus stays in the search input:
 * arrows move the highlighted row, Enter picks it. Attach `ref` to
 * `EntitySelector` and spread `inputProps` onto `EntitySelectorInput`.
 *
 * The selector deliberately binds no key handlers of its own — a mention menu
 * forwards these keys while focus sits in an editor, and a picker owns its own
 * input — so the wiring lives here, once, rather than at each call site.
 */
export function useSelectorKeyboardNav(): {
  ref: React.RefObject<EntitySelectorHandle | null>;
  /** Spread onto `EntitySelectorInput` — or, for a list with no search
   *  field, onto `EntitySelectorContent`, whose popup then holds focus and
   *  hears the same keys. */
  inputProps: { onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void };
} {
  const ref = React.useRef<EntitySelectorHandle>(null);
  const inputProps = React.useMemo(
    () => ({
      // Arrows would move the caret and Enter would submit an enclosing form,
      // so the nav keys are consumed here.
      onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          ref.current?.move(1);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          ref.current?.move(-1);
        } else if (e.key === "Enter") {
          e.preventDefault();
          ref.current?.selectActive();
        }
      },
    }),
    [],
  );
  return { ref, inputProps };
}
